import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import ClientManagerSidebar from './ClientManagerSidebar';
import ClientManagerHeader from './ClientManagerHeader';
import { usePreferenceActions } from '@/app/store/preferenceStore';
import { useAuthStore } from '@/app/store/authStore';
import axiosInstance from '@/app/api/axiosInstance';
import { toast } from 'sonner';
import { GlobalHotkeysProvider } from '@/app/components/GlobalHotkeysProvider';
import { cn } from '@/lib/utils';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ClientManagerLayout Component
 *
 * Layout specifically designed for client_manager role users.
 * Features:
 * - Simplified sidebar with only Dashboard, My Parties, and Tasks
 * - Task-focused header with task counters and quick create button
 * - Same responsive behavior as MainLayout
 * - Role-based access control (should be wrapped with RoleProtectedRoute)
 */
const ClientManagerLayout: React.FC = () => {
  const { loadPreferences } = usePreferenceActions();
  const { logoutAction } = useAuthStore();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Load user preferences when component mounts
    loadPreferences().catch((error) => {
      console.error('Failed to load preferences:', error);
    });

    // Add axios response interceptor to handle unauthorized errors
    const interceptorId = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check for unauthenticated error
        if (error?.response?.status === 401) {
          toast.error('Session expired. Please login again.', {
            id: 'session-expired',
          });
          logoutAction();
          navigate('/login');
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptor on component unmount
    return () => {
      axiosInstance.interceptors.response.eject(interceptorId);
    };
  }, [loadPreferences, logoutAction, navigate]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);

      // Auto-collapse sidebar on tablet
      if (width >= 768 && width < 1024) {
        setIsSidebarCollapsed(true);
      }

      // Close mobile sidebar when resizing to larger screen
      if (width >= 768) {
        setIsMobileSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  const handleMobileNavClick = () => {
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  };

  return (
    <GlobalHotkeysProvider>
      <div className="flex h-screen bg-background relative">
        {/* Mobile Sidebar Backdrop */}
        {isMobile && isMobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Client Manager Sidebar */}
        <div
          className={cn(
            'transition-all duration-300',
            // Mobile behavior
            isMobile && ['fixed inset-y-0 left-0 z-50', isMobileSidebarOpen ? 'w-64' : 'w-0'],
            // Tablet and desktop behavior
            !isMobile && ['relative flex-shrink-0', isSidebarCollapsed ? 'w-16' : 'w-64']
          )}
        >
          <div className={cn('h-full', isMobile && !isMobileSidebarOpen && 'hidden')}>
            <ClientManagerSidebar
              isCollapsed={isMobile ? false : isSidebarCollapsed}
              onToggle={handleSidebarToggle}
              onNavClick={handleMobileNavClick}
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Mobile Menu Button */}
          {isMobile && (
            <div className="sticky top-0 z-30 bg-background border-b px-4 py-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </div>
          )}

          <ClientManagerHeader isMobile={isMobile} />
          <main
            className={cn('flex-1 overflow-y-auto', 'p-4 sm:p-6 lg:p-8')}
          >
            <Outlet /> {/* Child routes render here */}
          </main>
        </div>
      </div>
    </GlobalHotkeysProvider>
  );
};

export default ClientManagerLayout;
